import React from 'react'
import {
  Button,
  PopUp,
  DataGrid,
  TableCell,
  Icon,
  theme,
  EmptyState,
  IconDictionary,
  Chip,
  Banner,
} from '@ketch-com/deck'
import { useRightInvocationsPaginatedV2 } from 'api/rightInvocationsV2/queries/useRightInvocationsPaginatedV2'
import { RightInvocationDTO, RightInvocationStatusDTO } from '@ketch-com/figurehead'
import { MaybeNull } from 'interfaces/common'
import { Box, Tooltip, Typography } from '@mui/material'
import { RightListStatusCellRendererV2 } from 'pages/orchestration/rightsQueue/listV2/components/RightListStatusCellRendererV2'
import { getTooltipContent } from 'utils/messages/getTooltipContent'
import { ApplicationEntity } from 'interfaces/applicationEntities/applicationEntity'
import { FormattedDateRenderer } from 'components/renderers/FormattedDateRenderer'
import { joinNonEmpty } from 'utils/helpers/joinNonEmpty'
import { EmptyValueRenderer } from 'components/EmptyValueRenderer'
import {
  FetchRightInvocationsFilterKeys,
  FetchRightInvocationsOrderByKeys,
} from 'interfaces/rightInvocationsV2/FetchRightInvocationsFilterKeys'
import { DataRendererWithTooltip } from 'pages/orchestration/rightsQueue/listV2/components/RightsQueueListTable/hooks/useRightsQueueListTableDataGridColumns'
import { GridRenderCellParams, GridColDef } from '@mui/x-data-grid-premium'
import { useNavigate } from 'react-router-dom'
import { RoutesManager } from 'utils/routing/routesManager'
import dayjs from 'dayjs'
import { useParams } from 'react-router-dom'
import { getURLParams } from 'utils/routing/routes/utils'

export type Props = {
  rightInvocation: MaybeNull<RightInvocationDTO>
  onClose: () => void
}

export const RightDuplicatesModal: React.FC<Props> = ({ onClose, rightInvocation }) => {
  const { id: rightInvocationId } = useParams<getURLParams>()
  const navigate = useNavigate()
  const oneYearAgo = dayjs().subtract(1, 'year').format('YYYY-MM-DD')

  /*
    we apply filters to the right invocations list to get the original and duplicate right invocations
    - original right invocation: the first right invocation in the list if sorted by invoked_at on ascending order
    - duplicate right invocations: all the right invocations in the list except the last one
  */
  const { data, isLoading: isLoadingRightInvocations } = useRightInvocationsPaginatedV2({
    itemsPerPage: 1000,
    params: {
      rightCodes: [rightInvocation?.rightCode || ''],
      query: rightInvocation?.user?.email,
      subjectTypeCodes: [rightInvocation?.user?.dataSubjectTypeCode || ''],
      invokedAtStart: oneYearAgo,
      ordering: 'asc',
      orderBy: 'invoked_at',
    },
  })

  let originalAndDuplicateRightInvocations: RightInvocationDTO[] = []

  const hasFulfilledRequests = data?.some(rightInvocation => {
    return rightInvocation?.status === RightInvocationStatusDTO.FulfilledRightInvocationStatus
  })

  /*
    - the last right invocation is the original right invocation only if it's status is fulfilled or pending
    - if the last right invocation is fulfilled then it is the original right invocation
    - if the last right invocation is pending then it is still original but we update tooltip accordingly
    - the rest are duplicates
  */
  const duplicateRightInvocations = data
    ?.filter((rightInvocation: RightInvocationDTO) => {
      const isFulfilled = rightInvocation?.status === RightInvocationStatusDTO.FulfilledRightInvocationStatus
      const isPending = rightInvocation?.status === RightInvocationStatusDTO.PendingRightInvocationStatus

      if (hasFulfilledRequests && !originalAndDuplicateRightInvocations.length && isFulfilled) {
        originalAndDuplicateRightInvocations.push(rightInvocation)
        return false
      }

      if (!hasFulfilledRequests && !originalAndDuplicateRightInvocations.length && isPending) {
        originalAndDuplicateRightInvocations.push(rightInvocation)
        return false
      }
      return true
    })
    .reverse()

  originalAndDuplicateRightInvocations = [...originalAndDuplicateRightInvocations, ...duplicateRightInvocations]

  const rightInvocationsColumns: GridColDef<RightInvocationDTO>[] = [
    {
      align: 'left',
      field: FetchRightInvocationsFilterKeys.statuses,
      headerAlign: 'left',
      headerName: 'Request Status',
      sortable: true,
      minWidth: 140,
      renderCell: ({ row: duplicateRightInvocation }: GridRenderCellParams<RightInvocationDTO>) => (
        <TableCell>
          <Box display="flex" alignItems="center">
            <RightListStatusCellRendererV2 rightInvocation={duplicateRightInvocation} />
            {duplicateRightInvocation.hasIssues && (
              <Tooltip title={getTooltipContent(ApplicationEntity.RIGHTS_QUEUE)}>
                <Box>
                  <Icon iconColor={theme.palette.chileanFire.main} name="FImportant" fontSize="small" />
                </Box>
              </Tooltip>
            )}
          </Box>
        </TableCell>
      ),
    },
    {
      align: 'left',
      field: 'subjectName',
      flex: 1,
      minWidth: 120,
      headerAlign: 'left',
      headerName: 'Subject Name',
      sortable: false,
      renderCell: ({ row: duplicateRightInvocation }: GridRenderCellParams<RightInvocationDTO>) => {
        const { user } = duplicateRightInvocation
        const subjectName = joinNonEmpty([user?.first, user?.last])
        return <TableCell>{user ? <Typography>{subjectName}</Typography> : <EmptyValueRenderer />}</TableCell>
      },
    },
    {
      align: 'left',
      field: 'requestCode',
      headerAlign: 'left',
      flex: 1,
      headerName: 'Request',
      minWidth: 160,
      sortable: false,
      renderCell: ({ row: duplicateRightInvocation }: GridRenderCellParams<RightInvocationDTO>) => {
        return <DataRendererWithTooltip rightInvocation={duplicateRightInvocation} />
      },
    },
    {
      align: 'left',
      field: 'workflow',
      headerAlign: 'left',
      flex: 1,
      minWidth: 130,
      headerName: 'Workflow',
      sortable: false,
      renderCell: ({ row: duplicateRightInvocation }: GridRenderCellParams<RightInvocationDTO>) => {
        const isAppealed = !!duplicateRightInvocation?.appeal?.isAppealed
        const workflowExecution = isAppealed
          ? duplicateRightInvocation?.appealWorkflowExecution
          : duplicateRightInvocation?.workflowExecution
        let workflowDefinitionName = workflowExecution?.workflowDefinitionName
        if (duplicateRightInvocation?.rightCode && duplicateRightInvocation.rightCode.startsWith('OT Workflow')) {
          workflowDefinitionName = duplicateRightInvocation?.rightCode
        }

        return workflowDefinitionName ? (
          <TableCell>
            <Typography title={workflowDefinitionName} variant="body">
              {workflowDefinitionName}
            </Typography>
          </TableCell>
        ) : (
          <TableCell>
            <EmptyValueRenderer />
          </TableCell>
        )
      },
    },
    {
      align: 'left',
      field: FetchRightInvocationsOrderByKeys.invoked_at,
      headerAlign: 'left',
      flex: 1,
      minWidth: 120,
      headerName: 'Invoked Date',
      sortable: false,
      renderCell: ({ row: duplicateRightInvocation }: GridRenderCellParams<RightInvocationDTO>) => {
        return (
          <TableCell>
            <FormattedDateRenderer date={duplicateRightInvocation?.invokedAt} format="MMM DD YYYY" />
          </TableCell>
        )
      },
    },
    {
      align: 'center',
      field: 'type',
      headerName: '',
      sortable: true,
      minWidth: 120,
      renderCell: ({ row: duplicateRightInvocation }: GridRenderCellParams<RightInvocationDTO>) => {
        const isFulfilledDsr =
          duplicateRightInvocation?.status === RightInvocationStatusDTO.FulfilledRightInvocationStatus
        const isPendingDsr = duplicateRightInvocation?.status === RightInvocationStatusDTO.PendingRightInvocationStatus
        const isOriginalRightInvocation =
          duplicateRightInvocation.id === originalAndDuplicateRightInvocations?.[0]?.id &&
          (isFulfilledDsr || isPendingDsr)

        return (
          <TableCell>
            <Chip
              color={isOriginalRightInvocation ? 'primary' : 'default'}
              size="small"
              label={
                <Typography variant="smallBody">{isOriginalRightInvocation ? 'Original' : 'Duplicate'}</Typography>
              }
            />
          </TableCell>
        )
      },
    },
    {
      align: 'center',
      field: 'id',
      flex: 1,
      headerName: '',
      sortable: false,
      renderCell: ({ row: duplicateRightInvocation }: GridRenderCellParams<RightInvocationDTO>) => {
        return (
          <TableCell>
            {duplicateRightInvocation.id !== rightInvocationId ? (
              <Button
                color="tertiary"
                size="small"
                onClick={() => {
                  navigate(
                    RoutesManager.orchestration.rightsQueue.view.root.getURL({
                      id: duplicateRightInvocation.id,
                    }),
                  )

                  onClose()
                }}
              >
                View
              </Button>
            ) : null}
          </TableCell>
        )
      },
    },
  ]

  return (
    <PopUp
      isLoading={isLoadingRightInvocations}
      title="Duplicate Requests"
      subTitle="Similar requests from the same email that were submitted within a period of less than 365 days"
      variant="modal"
      popUpWidth="980px"
      onClose={onClose}
      popUpActionChildren={
        <>
          <Button color="secondary" size="large" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      {!isLoadingRightInvocations && Boolean(!originalAndDuplicateRightInvocations.length) ? (
        <EmptyState
          size="small"
          colorTheme={theme.palette.Text.Secondary}
          iconName={IconDictionary.FCopy}
          title="Unable to find duplicate requests"
          subTitle="There are no similar requests from the same email that were submitted within a period of less than 365 days."
        />
      ) : null}

      {!isLoadingRightInvocations && Boolean(originalAndDuplicateRightInvocations.length) ? (
        <Box>
          {hasFulfilledRequests ? (
            <Banner
              sx={{
                flexShrink: 2,
                '&.MuiPaper-root': {
                  width: '100%',
                  marginBottom: 2,
                },
              }}
              severity="info"
              children={
                <Typography variant="smallBody">
                  The{' '}
                  <Typography variant="smallLabel">{originalAndDuplicateRightInvocations?.[0]?.rightName}</Typography>{' '}
                  was completed for{' '}
                  <Typography variant="smallLabel">{originalAndDuplicateRightInvocations?.[0]?.user?.email}</Typography>{' '}
                  on{' '}
                  <Typography variant="smallLabel">
                    <FormattedDateRenderer
                      date={originalAndDuplicateRightInvocations?.[0]?.invokedAt}
                      format="MMM DD YYYY"
                    />
                  </Typography>
                  , resulting in all subsequent requests within the following 365 days being marked as duplicates.
                </Typography>
              }
            />
          ) : null}
          {!hasFulfilledRequests ? (
            <Banner
              sx={{
                flexShrink: 2,
                '&.MuiPaper-root': {
                  width: '100%',
                  marginBottom: 2,
                },
              }}
              severity="info"
              children={
                <Typography variant="smallBody">
                  Please note: Multiple requests for the{' '}
                  <Typography variant="smallLabel">{originalAndDuplicateRightInvocations?.[0]?.rightName}</Typography>{' '}
                  associated with{' '}
                  <Typography variant="smallLabel">{originalAndDuplicateRightInvocations?.[0]?.user?.email}</Typography>{' '}
                  have been submitted within the past 365 days; however, none were completed.
                </Typography>
              }
            />
          ) : null}
          <Box height={300}>
            <DataGrid
              sx={{
                '.original-row': {
                  backgroundColor: theme.palette.Black.o8,
                  margin: '8px 0',
                  '&:hover': {
                    backgroundColor: theme.palette.Black.o8,
                  },
                },
              }}
              getRowHeight={() => 'auto'}
              getRowId={row => row?.id}
              rows={originalAndDuplicateRightInvocations}
              columns={rightInvocationsColumns}
              checkboxSelection={false}
              hideFooter={false}
              hideFooterPagination={false}
              hideFooterRowCount
              disableColumnMenu
              disableColumnPinning
              disableColumnReorder
              disableColumnResize
              disableChildrenSorting
              disableRowSelectionOnClick
              disableRowHoverStates
              loading={isLoadingRightInvocations}
              columnMenuProps={
                {
                  slots: {
                    columnMenuFilterItem: null,
                    columnMenuAggregationItem: null,
                    columnMenuGroupingItem: null,
                  },
                } as any
              }
              getRowClassName={({ row }) => {
                const isFulfilledDsr = row?.status === RightInvocationStatusDTO.FulfilledRightInvocationStatus
                const isPendingDsr = row?.status === RightInvocationStatusDTO.PendingRightInvocationStatus
                const isOriginalRightInvocation =
                  row?.id === originalAndDuplicateRightInvocations?.[0]?.id && (isFulfilledDsr || isPendingDsr)

                if (isOriginalRightInvocation) {
                  return 'original-row'
                }
                return ''
              }}
            />
          </Box>
        </Box>
      ) : null}
    </PopUp>
  )
}
